// api/core/chameleonState.ts
// Single source of truth for the chameleon/demo impersonation identity.
//
// sessionStorage IS the state. It is read synchronously on every outbound request
// rather than mirrored into a module variable, because a mirror is only ever as
// fresh as its last writer: any request firing before ChameleonProvider mounts —
// or after any future code path changes the identity without a reload — would go
// out wearing the wrong entity, and the backend would answer for whoever it was
// told. Reading storage at request time makes that class of bug unrepresentable.
//
// Backend boundary is `security/secure_access.py`; for the demo agency the check is
// an exact `entity_type == AGENCY and entity_id == 118` match. Do not loosen the
// header semantics without reading it.

export const CHAMELEON_STORAGE_KEY = 'chameleon_session';

export type ChameleonEntityType = 'broadcaster' | 'customer' | 'agency' | 'adtech';

export interface ChameleonSession {
    active: boolean;
    entityId: number | null;
    entityType: ChameleonEntityType | null;
    entityName: string | null;
}

export const INACTIVE_CHAMELEON: ChameleonSession = {
    active: false,
    entityId: null,
    entityType: null,
    entityName: null,
};

/** The active impersonation, or null when the user is wearing their own face. */
export const readChameleonSession = (): ChameleonSession | null => {
    try {
        const raw = sessionStorage.getItem(CHAMELEON_STORAGE_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw) as ChameleonSession;
        if (!parsed?.active || !parsed.entityType || parsed.entityId == null) return null;

        return parsed;
    } catch {
        // Corrupt storage — behave as though not impersonating. Failing closed here
        // means the user sees their own data, never someone else's.
        return null;
    }
};

export const writeChameleonSession = (session: ChameleonSession): void => {
    sessionStorage.setItem(CHAMELEON_STORAGE_KEY, JSON.stringify(session));
};

export const clearChameleonSession = (): void => {
    sessionStorage.removeItem(CHAMELEON_STORAGE_KEY);
};

/**
 * Impersonation headers for the current request. Empty object when inactive —
 * spread it into every outbound call.
 */
export const getChameleonHeaders = (): Record<string, string> => {
    const session = readChameleonSession();
    if (!session?.entityType || session.entityId == null) return {};

    return {
        'X-Impersonate-Entity-Type': session.entityType,
        'X-Impersonate-Entity-Id': String(session.entityId),
    };
};
